import PropTypes from 'prop-types';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Card } from 'semantic-ui-react';
import Image from '@plone/volto/components/theme/Image/Image';

import LogoImage from '../../customizations/components/theme/Logo/Logo.svg';
import { getHomepageContentUrl } from '../../helpers/api';
import { extractHomepageContact } from '../../helpers/homepageContact';
import { getActiveLanguage } from '../../helpers/homepageUrl';

const messages = defineMessages({
  address: {
    id: 'Address',
    defaultMessage: 'Adreça',
  },
  phone: {
    id: 'Phone',
    defaultMessage: 'Telèfons',
  },
  email: {
    id: 'Email',
    defaultMessage: 'Email',
  },
});

const parseContactInfo = (content, format, editPreview = false) =>
  content.split('\r\n').map((line, index) => {
    const start = line.indexOf('{');
    const end = line.indexOf('}');
    if (start >= 0 && end > start) {
      const before = line.slice(0, start);
      const data = line.slice(start + 1, end);
      const after = line.slice(end + 1);
      return (
        <Fragment key={index}>
          {before}
          {editPreview ? (
            <span>{data}</span>
          ) : (
            <a href={`${format}${data.replace(/\s/g, '')}`}>{data}</a>
          )}
          {after}
        </Fragment>
      );
    }
    return <Fragment key={index}>{line}</Fragment>;
  });

const ContactCardView = ({ content, editPreview = false }) => {
  const intl = useIntl();
  const { pathname } = useLocation();
  const locale = useSelector((state) => state.intl.locale);
  const language = getActiveLanguage(pathname, locale);
  const fromContent = extractHomepageContact(content);
  const hasContact = Boolean(fromContent);
  const [fetched, setFetched] = useState(null);
  const data = fromContent || fetched;

  useEffect(() => {
    if (hasContact) {
      return undefined;
    }

    const url = getHomepageContentUrl(language);
    const controller = new AbortController();

    fetch(url, {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((payload) => {
        const contact = extractHomepageContact(payload);
        if (contact) {
          setFetched(contact);
        }
      })
      .catch(() => {});

    return () => controller.abort();
  }, [hasContact, language]);

  const telefons = useMemo(
    () =>
      data?.telefons
        ? parseContactInfo(data.telefons, 'tel:', editPreview)
        : null,
    [data?.telefons, editPreview],
  );
  const emails = useMemo(
    () =>
      data?.email ? parseContactInfo(data.email, 'mailto:', editPreview) : null,
    [data?.email, editPreview],
  );

  return (
    <Card className="contact-card">
      <Image
        className="ui image"
        src={LogoImage}
        alt="Clínica Sant Josep"
        width={960}
        height={326}
        responsive
        loading="lazy"
      />

      {data?.adreca && (
        <>
          <p className="contact-heading">
            {intl.formatMessage(messages.address)}
          </p>
          {data.adreca.split('\r\n').map((line, index) => (
            <p key={index} className="contact-text">
              {line}
            </p>
          ))}
        </>
      )}

      {telefons && (
        <>
          <p className="contact-heading">
            {intl.formatMessage(messages.phone)}
          </p>
          {telefons.map((telefon, index) => (
            <p key={index} className="contact-text">
              {telefon}
            </p>
          ))}
        </>
      )}

      {emails && (
        <>
          <p className="contact-heading">
            {intl.formatMessage(messages.email)}
          </p>
          {emails.map((email, index) => (
            <p key={index} className="contact-text">
              {email}
            </p>
          ))}
        </>
      )}
    </Card>
  );
};

ContactCardView.propTypes = {
  content: PropTypes.objectOf(PropTypes.any),
  editPreview: PropTypes.bool,
};

export default ContactCardView;
